import React from 'react';

import { makeStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import Container from '@material-ui/core/Container';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardMedia from '@material-ui/core/CardMedia';
import Hidden from '@material-ui/core/Hidden';
import Typography from '@material-ui/core/Typography';
import { Grid, CircularProgress, Box } from '@material-ui/core';

const useStyles = makeStyles(theme => ({
    wrapper: {
        background: '#043954',
        border: `1px solid #05C3DE`,
        borderRadius: 4,
        boxSizing: 'border-box',
        height: 40,
    },
    containerRoot: {
        [theme.breakpoints.up('md')]: {
            paddingLeft: theme.spacingVars.xmedium,
            paddingRight: theme.spacingVars.xmedium,
            maxWidth: '1280px'
        },
    },
    card: {
        minHeight: 244,
        maxWidth: '100%',
        marginBottom: '120px',
        marginTop: theme.fontVars.xxxlarge,
        borderTop: '4px solid #05C3DE',
        borderRadius: '0px',
        boxShadow: theme.boxShadow,
        '&:hover': {
            boxShadow: theme.boxShadowHover,
        },
    },
    cardError: {
        minHeight: 244,
        marginBottom: '120px',
        marginTop: theme.fontVars.xxxlarge,
        borderTop: '4px solid #E17A17',
        borderRadius: '0px',
        boxShadow: theme.boxShadowError,
        '&:hover': {
            boxShadow: theme.boxShadowErrorHover,
        },
    },
    cardSuccess: {
        minHeight: 244,
        marginBottom: '120px',
        marginTop: theme.fontVars.xxxlarge,
        borderTop: '4px solid #7D9845',
        borderRadius: '0px',
        boxShadow: theme.boxShadow,
        '&:hover': {
            boxShadow: theme.boxShadowHover,
        },
    },
    details: {
        display: 'flex',
        flexDirection: 'row',
        height: 'inherit',
    },
    content: {
        padding: theme.spacingVars.xlarge,
        '&:last-child': {
            paddingBottom: theme.spacingVars.xlarge,
        },
        [theme.breakpoints.down('sm')]: {
            padding: theme.spacingVars.xxxmedium
        }
    },
    cardContentText: {
        display: 'flex',
        justifyContent: 'center',
        color: theme.colors.darkGray,
    },
    cardHeaderText: {
        fontSize: theme.fontVars.h4Font,
        lineHeight: theme.fontVars.h4FontLH,
        fontFamily: theme.fontFamily.reg,
        color: theme.colors.darkGray,
        [theme.breakpoints.down('xs')]: {
            fontSize: theme.fontVars.xlarge
        }
    },
    cardDataText: {
        fontSize: theme.fontVars.large,
        lineHeight: '26px',
        fontFamily: theme.fontFamily.light,
        paddingTop: theme.spacingVars.medium,
        color: theme.darkGray,
    },
    cardMediaTextTitle: {
        paddingLeft: theme.spacingVars.xlarge,
        lineHeight: '24px',
        fontFamily: theme.fontFamily.bold,
        color: theme.colors.white,
        fontSize: theme.fontVars.xlarge
    },
    cardMediaText: {
        paddingLeft: theme.spacingVars.xlarge,
        lineHeight: '24px',
        fontFamily: theme.fontFamily.light,
        color: theme.colors.white,
        paddingTop: 4,
        fontSize: theme.fontVars.xlarge
    },
    secondaryText: {
        fontSize: theme.fontVars.large,
        lineHeight: '24px',
        fontFamily: theme.fontFamily.light,
        paddingTop: theme.spacingVars.xxmedium,
        color: theme.colors.darkGray,
    },
    smallSecondaryText: {
        fontSize: '16px',
        lineHeight: '22px',
        fontFamily: 'Nimbus Sans Reg',
        paddingTop: '20px',
        color: '#3b3b3b',
    },
    cardDataSubText: {
        fontSize: theme.fontVars.large,
        lineHeight: '24px',
        fontFamily: theme.fontFamily.light,
        paddingTop: '26px',
    },
    imageCover: {
        width: 191,
        height: 181,
    },
    cardButton: {
        boxShadow: 'none',
        background: theme.colors.interactiveBlue,
        minWidth: '196px',
        padding: '8px 16px',
        '&:hover': {
            backgroundColor: theme.colors.hoverBlue,
        },
        marginTop: theme.spacingVars.xlarge,
        fontSize: theme.fontVars.medium,
        fontFamily: theme.fontFamily.bold,
        color: theme.colors.white,
        textTransform: 'capitalize',
        marginRight: theme.spacingVars.xmedium,
        marginBottom: theme.spacingVars.small,
        [theme.breakpoints.down('sm')]: {
            width: '100%',
        },
    },
    cardMediaButton: {
        boxShadow: 'none',
        background: 'none',
        minWidth: '196px',
        border: '1px solid #FFFFFF',
        borderRadius: '5px',
        borderColor: theme.colors.white,
        padding: '6px 16px',
        '&:hover': {
            backgroundColor: theme.colors.hoverBlue,
        },
        marginTop: theme.spacingVars.xxxmedium,
        fontSize: theme.fontVars.medium,
        fontFamily: theme.fontFamily.reg,
        color: theme.colors.white,
        textTransform: 'capitalize',
        marginRight: theme.spacingVars.xmedium,
        marginBottom: theme.spacingVars.xlarge,
        [theme.breakpoints.down('sm')]: {
            width: '100%',
        },

    },
    disabled: {},
    secondaryCardButton: {
        boxShadow: 'none',
        background: theme.colors.white,
        border: '1px solid #086EA1',
        padding: '8px 16px',
        whiteSpace: 'nowrap',
        minWidth: '196px',
        '&:hover': {
            backgroundColor: theme.colors.hoverBlue,
            color: theme.colors.white,
            border: '1px solid #043954',
        },
        marginTop: theme.spacingVars.xlarge,
        marginBottom: theme.spacingVars.small,
        fontSize: theme.fontVars.medium,
        maxHeight: 45,
        fontFamily: theme.fontFamily.reg,
        color: theme.colors.interactiveBlue,
        textTransform: 'capitalize',
        [theme.breakpoints.down('sm')]: {
            width: '100%',
        },
    },
    buttonProgress: {
        color: theme.colors.brightBlue,
        marginLeft: theme.spacingVars.small,
        verticalAlign: 'sub',
    },
    cardImage: {
        marginLeft: 'auto',
        marginRight: 0,
        backgroundColor: theme.colors.interactiveBlue,
        minWidth: '392px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: 244,
        textTransform: 'capitalize',
        [theme.breakpoints.down('sm')]: {
            width: '100%',
            minWidth: 'auto'
        }
    },
    mobileImage: {
        width: '24%',
        padding: '24px 24px 0',
        maxWidth: '120px'
    },
    alternateMediaImage: {
        padding: '32px 32px 24px',
        maxWidth: '120px'
    },
    tooltip: {},
}));

const CardButton = ({ secondaryButton, mediaButton, id, onClick, cardButtonText, isLoading, classes, href, target }) => {
    const buttonRoot = () => {
        if (secondaryButton) {
            return classes.secondaryCardButton
        }
        if (mediaButton) {
            return classes.cardMediaButton
        }
        return classes.cardButton
    };

    const hrefTarget = () => {
        if (target) {
            return target;
        }
        if (href) {
            return '_blank';
        }
        return '';
    };

    return (
        <div>
            <Button
                id={`${id}${isLoading ? '-loading' : ''}`}
                size="large"
                classes={{ root: buttonRoot(), disabled: classes.disabled }}
                onClick={onClick}
                color="primary"
                variant="contained"
                href={href}
                target={hrefTarget()}
                disabled={isLoading}>
                <span>
                    {!isLoading ? cardButtonText : ''}
                    {isLoading && <CircularProgress size={20} className={classes.buttonProgress} alt='loading' id='loading-spinner' />}
                </span>
            </Button>
        </div>
    );
}

export default function CardWithMedia({
    errorCardClass,
    successCardClass,
    headerText,
    title,
    cardMedia,
    text,
    cardDataSubText,
    secondaryText,
    smallSecondaryText,
    smallSubText,
    onClick,
    id,
    secondaryId,
    cardButtonText,
    secondaryCardButtonText,
    secondaryOnClick,
    children,
    statusHeader,
    isLoading,
    href,
    target,
    noImageInMobile = false,
}) {
    const classes = useStyles();
    let cardClass = classes.card;
    if (errorCardClass) {
        cardClass = classes.cardError;
    } else if (successCardClass) {
        cardClass = classes.cardSuccess;
    }

    /* eslint-disable no-mixed-operators */
    /* eslint-disable  react/no-danger */
    return (
        <div>
            <Container maxWidth="xl" className={classes.containerRoot} id={`${id}-media-card`}>
                <Card className={cardClass} style={{ marginBottom: statusHeader ? '32px' : '' }}>
                  {!noImageInMobile && (
                    <Hidden mdUp>
                      <Box>
                        <img className={classes.mobileImage} src={cardMedia} alt={title} />
                      </Box>
                    </Hidden>
                  )}
                    <div className={classes.details}>
                        <div className={classes.cardContentText}>
                            <CardContent className={classes.content}>
                                <Typography component="h5" variant="h5">
                                    <div className={classes.cardHeaderText} id={`${id}-media-title`}>{headerText}</div>
                                    <div className={classes.cardDataText} id={`${id}-media-main-content`} dangerouslySetInnerHTML={{__html: text}} />
                                    {secondaryText && (
                                        <div className={classes.secondaryText} id={`${id}-media-secondary-content`} dangerouslySetInnerHTML={{__html: secondaryText}} />
                                    )}
                                    {cardDataSubText && (
                                        <div className={classes.cardDataSubText} id={`${id}-media-sub-text`} dangerouslySetInnerHTML={{__html: cardDataSubText}} />
                                    )}
                                    {smallSecondaryText && (
                                        <div className={classes.smallSecondaryText} id={`${id}-media-small-secondary-text`} dangerouslySetInnerHTML={{__html: smallSecondaryText}} />
                                    )}
                                    {smallSubText && (
                                        <div className={classes.smallSecondaryText} id={`${id}-media-small-sub-text`} dangerouslySetInnerHTML={{__html: smallSubText}} />
                                    )}
                                </Typography>
                                <Grid container>
                                    {cardButtonText && (
                                        <Grid item xs={12} lg={4} md={6}>
                                            <CardButton
                                                className={classes.cardButton}
                                                id={`${id}-media-button`}
                                                onClick={onClick}
                                                cardButtonText={cardButtonText}
                                                isLoading={isLoading}
                                                classes={classes}
                                                secondaryButton={false}
                                                href={href}
                                                target={target}
                                            />
                                        </Grid>
                                    )}
                                    {secondaryCardButtonText && (
                                        <Grid item xs={12} lg={4} md={6} >
                                            <CardButton
                                                className={classes.secondaryCardButton}
                                                id={`${secondaryId}-media-button`}
                                                onClick={secondaryOnClick}
                                                cardButtonText={secondaryCardButtonText}
                                                classes={classes}
                                                secondaryButton
                                            />
                                        </Grid>
                                    )}
                                </Grid>
                                {children}
                            </CardContent>
                        </div>
                        <Hidden smDown>
                            <div className={classes.cardImage}>
                                <CardMedia
                                    className={classes.imageCover}
                                    image={cardMedia}
                                    title={title}
                                    id={`${id}-media-image`}
                                />
                            </div>
                        </Hidden>
                    </div>
                </Card>
            </Container>
        </div>
    );
}
